import SmallBadge from "@/common/components/SmallBadge";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { FilePlusIcon } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryData } from "../../../types/updatedSummary.type";
import CreateAuctionProposal from "./CreateAuctionProposal";

interface CreateAuctionProposalButtonProps {
  auctionEntryId: string | undefined;
  auctionSummary: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryData | undefined;
  disabled?: boolean;
}

const CreateAuctionProposalButton = ({
  auctionEntryId,
  auctionSummary,
  disabled = false,
}: CreateAuctionProposalButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const tooltipId = useId();

  const navigate = useNavigate();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const hasCreatePermission = !isUserPermissionsPending
    ? hasUserPermission('Establishment::Maintenance::EstablishmentChoice::AuctionEntryProposals::Create')
    : true;

  const isEffectivelyDisabled = disabled || !hasCreatePermission;

  const tooltipContent = !hasCreatePermission
    ? 'Você não tem permissão'
    : 'Não é possível enviar proposta em leilão pausado';

  const handleClick = useCallback(() => {
    if (disabled || !hasCreatePermission || !auctionSummary?.directive_entry_type) {
      return;
    }

    if (auctionSummary?.directive_entry_type === MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry) {
      return navigate(`/maintenance-establishment-choice/auction-entries/${auctionEntryId}/create-execution-proposal`);
    }

    setIsOpen(true);
  }, [disabled, hasCreatePermission, auctionSummary?.directive_entry_type, auctionEntryId]);

  return (
    <>
      <SmallBadge
        data-tooltip-id={isEffectivelyDisabled && !isUserPermissionsPending ? tooltipId : undefined}
        LeftIcon={FilePlusIcon}
        text="Nova proposta"
        className={cn(
          'bg-primary-blue hover:bg-primary-blue-hover transition-colors cursor-pointer',
          isEffectivelyDisabled && 'opacity-50 cursor-not-allowed hover:bg-primary-blue',
        )}
        onClick={handleClick}
      />

      {isEffectivelyDisabled && !isUserPermissionsPending && (
        <Tooltip id={tooltipId} style={{ zIndex: 50 }}>
          {tooltipContent}
        </Tooltip>
      )}

      <CreateAuctionProposal
        auctionEntryId={auctionEntryId}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
    </>
  );
}

export default CreateAuctionProposalButton;
